// components
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import Chart, { useChart } from 'src/components/chart';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { fPercent, formatNumber } from 'src/utils/format-number';
import { generateAnnotations } from 'src/utils/graph-demo-annotations';
import { useRef, useState } from 'react';

// ----------------------------------------------------------------------

type Props = {
  series: {
    name: string;
    data: number[];
  }[];
  labels: string[];

  /** Override the per-series fill colors. Falls back to the legacy palette when omitted. */
  colors?: string[];

  /** Top radius applied to the last stacked segment of each column. */
  barBorderRadius?: number;

  /** Render the percentage of each segment on the bars. */
  showDataLabels?: boolean;

  /** Diameter (px) of the round legend markers. */
  legendMarkerSize?: number;

  /** Override the legend label text color. */
  legendLabelColor?: string;

  /** Chart render height (px). */
  height?: number;

  /** Hide the built-in ApexCharts legend (use when rendering a custom legend outside). */
  hideLegend?: boolean;

  /** Opt-in absolute-total rows appended below the per-series rows in the tooltip */
  extraTooltipRows?: {
    name: string;
    color: string;
    compute: (
      values: number[],
      names: string[]
    ) => number;
  }[];
};

// ----------------------------------------------------------------------

export default function ChartColumnStackedPercent({
  series,
  labels,
  colors,
  barBorderRadius = 0,
  showDataLabels = false,
  legendMarkerSize = 16,
  legendLabelColor,
  height = 350,
  hideLegend = false,
  extraTooltipRows,
}: Props) {
  const theme = useTheme();

  const filterStore = useSelector(
    (state: any) => state.filters
  );

  const workspace =
    useCurrentWorkspace();

  const chartRef =
    useRef(null);

  const chartContainerRef =
    useRef<HTMLDivElement | null>(
      null
    );

  const [
    hiddenSeries,
    setHiddenSeries,
  ] = useState<Set<string>>(
    () => new Set()
  );

  const annotations =
    generateAnnotations(
      workspace,
      filterStore,
      labels
    );

  enum ColorSchema {
    PRIMARY = 'primary',
    SECONDARY = 'secondary',
    TERTIARY = 'tertiary',
    ERROR = 'error',
    WARNING = 'warning',
    INFO = 'info',
    SUCCESS = 'success',
  }

  const resolvedColors =
    colors ??
    (
      series?.length === 3
        ? [
            theme.palette[
              ColorSchema.SUCCESS
            ].light,
            theme.palette[
              ColorSchema.TERTIARY
            ].light,
            '#b7b7b7',
          ]
        : series?.length === 5
          ? [
              theme.palette[
                ColorSchema.SUCCESS
              ].light,
              '#1e88e5',
              '#64b5f6',
              '#b7dcfb',
              '#b7b7b7',
            ]
          : [
              '#118d57',
              '#1e88e5',
              '#64b5f6',
              '#E1E1E1',
            ]
    );

  // --------------------------------------------------------------------
  // Custom annotation labels
  // --------------------------------------------------------------------

  const labelAnnotations =
    showDataLabels
      ? series.flatMap(
          (
            serie,
            seriesIndex
          ) => {
            if (
              hiddenSeries.has(
                serie.name
              )
            ) {
              return [];
            }

            return serie.data
              .map(
                (
                  value,
                  dataPointIndex
                ) => {
                  const numericValue =
                    Number(value);

                  if (
                    !Number.isFinite(
                      numericValue
                    ) ||
                    numericValue <= 0
                  ) {
                    return null;
                  }

                  /*
                   * Pour un stacked 100% :
                   * le Y de l'annotation doit correspondre
                   * au cumul des pourcentages des séries visibles.
                   */

                  let visibleTotal = 0;

                  series.forEach(
                    (
                      currentSeries
                    ) => {
                      if (
                        hiddenSeries.has(
                          currentSeries.name
                        )
                      ) {
                        return;
                      }

                      const currentValue =
                        Number(
                          currentSeries
                            .data[
                            dataPointIndex
                          ]
                        );

                      if (
                        Number.isFinite(
                          currentValue
                        ) &&
                        currentValue > 0
                      ) {
                        visibleTotal +=
                          currentValue;
                      }
                    }
                  );

                  if (
                    visibleTotal <= 0
                  ) {
                    return null;
                  }

                  let cumulativeValue = 0;

                  for (
                    let i = 0;
                    i <= seriesIndex;
                    i += 1
                  ) {
                    const currentSeries =
                      series[i];

                    if (
                      hiddenSeries.has(
                        currentSeries.name
                      )
                    ) {
                      continue;
                    }

                    const currentValue =
                      Number(
                        currentSeries
                          .data[
                          dataPointIndex
                        ]
                      );

                    if (
                      Number.isFinite(
                        currentValue
                      ) &&
                      currentValue > 0
                    ) {
                      cumulativeValue +=
                        currentValue;
                    }
                  }

                  const percentage =
                    (
                      numericValue /
                      visibleTotal
                    ) * 100;

                  const cumulativePercent =
                    (
                      cumulativeValue /
                      visibleTotal
                    ) * 100;

                  return {
                    x:
                      labels[
                        dataPointIndex
                      ],

                    /*
                     * stackType 100% => axe 0..100
                     */
                    y:
                      cumulativePercent,

                    marker: {
                      size: 0,
                    },

                    label: {
                      text:
                        `${percentage.toFixed(
                          0
                        )}%`,

                      /*
                       * Comme pour tes autres charts :
                       * juste au niveau du sommet du segment.
                       */
                      offsetY: 7,

                      borderColor:
                        'rgba(145, 158, 171, 0.20)',

                      borderWidth: 1,

                      borderRadius: 4,

                      style: {
                        background:
                          '#FFFFFF',

                        color:
                          '#1C252E',

                        fontSize:
                          '11px',

                        fontWeight:
                          500,

                        fontFamily:
                          'Public Sans, sans-serif',

                        cssClass:
                          `chart-data-label chart-data-label-series-${seriesIndex}`,
                      },
                    },
                  };
                }
              )
              .filter(Boolean);
          }
        )
      : [];

  // --------------------------------------------------------------------
  // Rebuild annotation labels like:
  //
  // display: inline-flex;
  // padding: 4px 8px;
  // justify-content: center;
  // align-items: center;
  // gap: 4px;
  // --------------------------------------------------------------------

  const injectSeriesDotsIntoLabels =
    () => {
      requestAnimationFrame(
        () => {
          const container =
            chartContainerRef.current;

          if (!container) {
            return;
          }

          container
            .querySelectorAll(
              '.chart-label-dot'
            )
            .forEach(
              (dot) =>
                dot.remove()
            );

          series.forEach(
            (
              serie,
              seriesIndex
            ) => {
              if (
                hiddenSeries.has(
                  serie.name
                )
              ) {
                return;
              }

              const seriesColor =
                resolvedColors[
                  seriesIndex
                ] ||
                '#637381';

              const textElements =
                container.querySelectorAll(
                  `text.chart-data-label-series-${seriesIndex}`
                );

              textElements.forEach(
                (node) => {
                  const text =
                    node as SVGTextElement;

                  // ----------------------------------------------
                  // Find corresponding rect
                  // ----------------------------------------------

                  let rect =
                    text.previousElementSibling as SVGRectElement | null;

                  if (
                    !rect ||
                    rect.tagName.toLowerCase() !==
                      'rect'
                  ) {
                    const parent =
                      text.parentElement;

                    if (!parent) {
                      return;
                    }

                    const siblings =
                      Array.from(
                        parent.children
                      );

                    const textIndex =
                      siblings.indexOf(
                        text
                      );

                    for (
                      let i =
                        textIndex - 1;
                      i >= 0;
                      i -= 1
                    ) {
                      if (
                        siblings[
                          i
                        ].tagName.toLowerCase() ===
                        'rect'
                      ) {
                        rect =
                          siblings[
                            i
                          ] as SVGRectElement;

                        break;
                      }
                    }
                  }

                  if (!rect) {
                    return;
                  }

                  // ----------------------------------------------
                  // Inline-flex design
                  // ----------------------------------------------

                  const PADDING_X =
                    8;

                  const PADDING_Y =
                    4;

                  const GAP = 4;

                  const DOT_RADIUS =
                    3;

                  const DOT_SIZE =
                    DOT_RADIUS * 2;

                  // ----------------------------------------------
                  // Original Apex dimensions
                  // ----------------------------------------------

                  const originalXAttribute =
                    rect.getAttribute(
                      'data-original-x'
                    );

                  const originalYAttribute =
                    rect.getAttribute(
                      'data-original-y'
                    );

                  const originalWidthAttribute =
                    rect.getAttribute(
                      'data-original-width'
                    );

                  const originalHeightAttribute =
                    rect.getAttribute(
                      'data-original-height'
                    );

                  const originalX =
                    originalXAttribute !==
                    null
                      ? Number(
                          originalXAttribute
                        )
                      : Number(
                          rect.getAttribute(
                            'x'
                          ) || 0
                        );

                  const originalY =
                    originalYAttribute !==
                    null
                      ? Number(
                          originalYAttribute
                        )
                      : Number(
                          rect.getAttribute(
                            'y'
                          ) || 0
                        );

                  const originalWidth =
                    originalWidthAttribute !==
                    null
                      ? Number(
                          originalWidthAttribute
                        )
                      : Number(
                          rect.getAttribute(
                            'width'
                          ) || 0
                        );

                  const originalHeight =
                    originalHeightAttribute !==
                    null
                      ? Number(
                          originalHeightAttribute
                        )
                      : Number(
                          rect.getAttribute(
                            'height'
                          ) || 0
                        );

                  if (
                    originalXAttribute ===
                    null
                  ) {
                    rect.setAttribute(
                      'data-original-x',
                      String(
                        originalX
                      )
                    );
                  }

                  if (
                    originalYAttribute ===
                    null
                  ) {
                    rect.setAttribute(
                      'data-original-y',
                      String(
                        originalY
                      )
                    );
                  }

                  if (
                    originalWidthAttribute ===
                    null
                  ) {
                    rect.setAttribute(
                      'data-original-width',
                      String(
                        originalWidth
                      )
                    );
                  }

                  if (
                    originalHeightAttribute ===
                    null
                  ) {
                    rect.setAttribute(
                      'data-original-height',
                      String(
                        originalHeight
                      )
                    );
                  }

                  // ----------------------------------------------
                  // Center calculated by Apex
                  // ----------------------------------------------

                  const centerX =
                    originalX +
                    originalWidth / 2;

                  const centerY =
                    originalY +
                    originalHeight / 2;

                  // ----------------------------------------------
                  // Actual text dimensions
                  // ----------------------------------------------

                  let textBBox;

                  try {
                    textBBox =
                      text.getBBox();
                  } catch {
                    return;
                  }

                  const textWidth =
                    textBBox.width;

                  const textHeight =
                    textBBox.height;

                  // ----------------------------------------------
                  // Final chip dimensions
                  //
                  // 8 + dot 6 + gap 4 + text + 8
                  // ----------------------------------------------

                  const chipWidth =
                    PADDING_X +
                    DOT_SIZE +
                    GAP +
                    textWidth +
                    PADDING_X;

                  const chipHeight =
                    PADDING_Y +
                    textHeight +
                    PADDING_Y;

                  // ----------------------------------------------
                  // Keep the WHOLE chip centered
                  // ----------------------------------------------

                  const chipX =
                    centerX -
                    chipWidth / 2;

                  const chipY =
                    centerY -
                    chipHeight / 2;

                  rect.setAttribute(
                    'x',
                    String(chipX)
                  );

                  rect.setAttribute(
                    'y',
                    String(chipY)
                  );

                  rect.setAttribute(
                    'width',
                    String(
                      chipWidth
                    )
                  );

                  rect.setAttribute(
                    'height',
                    String(
                      chipHeight
                    )
                  );

                  rect.setAttribute(
                    'rx',
                    '4'
                  );

                  rect.setAttribute(
                    'ry',
                    '4'
                  );

                  // ----------------------------------------------
                  // Dot
                  // ----------------------------------------------

                  const dotCenterX =
                    chipX +
                    PADDING_X +
                    DOT_RADIUS;

                  const dotCenterY =
                    centerY;

                  // ----------------------------------------------
                  // Text after dot + gap
                  // ----------------------------------------------

                  const textStartX =
                    chipX +
                    PADDING_X +
                    DOT_SIZE +
                    GAP;

                  text.setAttribute(
                    'text-anchor',
                    'start'
                  );

                  text.setAttribute(
                    'x',
                    String(
                      textStartX
                    )
                  );

                  text.setAttribute(
                    'y',
                    String(
                      centerY
                    )
                  );

                  text.setAttribute(
                    'dominant-baseline',
                    'middle'
                  );

                  // ----------------------------------------------
                  // Create colored dot
                  // ----------------------------------------------

                  const circle =
                    document.createElementNS(
                      'http://www.w3.org/2000/svg',
                      'circle'
                    );

                  circle.setAttribute(
                    'class',
                    `chart-label-dot chart-label-dot-series-${seriesIndex}`
                  );

                  circle.setAttribute(
                    'cx',
                    String(
                      dotCenterX
                    )
                  );

                  circle.setAttribute(
                    'cy',
                    String(
                      dotCenterY
                    )
                  );

                  circle.setAttribute(
                    'r',
                    String(
                      DOT_RADIUS
                    )
                  );

                  circle.setAttribute(
                    'fill',
                    seriesColor
                  );

                  circle.setAttribute(
                    'stroke',
                    'none'
                  );

                  text.parentNode?.insertBefore(
                    circle,
                    text
                  );
                }
              );
            }
          );
        }
      );
    };

  // --------------------------------------------------------------------
  // Chart options
  // --------------------------------------------------------------------

  const chartOptions =
    useChart({
      chart: {
        stacked: true,
        type: 'bar',
        stackType: '100%',

        events: {
          mounted: () => {
            injectSeriesDotsIntoLabels();
          },

          updated: () => {
            injectSeriesDotsIntoLabels();
          },

          legendClick: (
            chartContext: any,
            seriesIndex: number,
            config: any
          ) => {
            const seriesName =
              config?.config
                ?.series?.[
                seriesIndex
              ]?.name;

            if (!seriesName) {
              return;
            }

            setHiddenSeries(
              (previous) => {
                const next =
                  new Set(
                    previous
                  );

                if (
                  next.has(
                    seriesName
                  )
                ) {
                  next.delete(
                    seriesName
                  );
                } else {
                  next.add(
                    seriesName
                  );
                }

                return next;
              }
            );
          },
        },
      },

      series,

      fill: {
        colors:
          resolvedColors,
      },

      colors:
        resolvedColors,

      labels:
        labels || [],

      xaxis: {
        labels: {
          offsetY: 4,
        },
      },

      grid: {
        padding: {
          top:
            showDataLabels
              ? 20
              : 0,

          bottom: 8,
        },
      },

      legend: {
        show:
          !hideLegend,

        itemMargin: {
          vertical: 8,
        },

        markers: {
          customHTML:
            function () {
              return `
                <div
                  style="
                    width: ${legendMarkerSize}px;
                    height: ${legendMarkerSize}px;
                    border-radius: 50%;
                    background-color: currentcolor;
                  "
                ></div>
              `;
            },
        },

        position: 'top',

        ...(legendLabelColor
          ? {
              labels: {
                colors:
                  legendLabelColor,
              },
            }
          : {}),
      },

      /*
       * Native Apex labels disabled.
       */
      dataLabels: {
        enabled: false,
      },

      plotOptions: {
        bar: {
          barHeight:
            '80%',

          columnWidth:
            '70%',

          borderRadius:
            barBorderRadius,

          distributed:
            false,

          borderRadiusApplication:
            'end',

          borderRadiusWhenStacked:
            'last',
        },
      },

      stroke: {
        width: 0,
        show: false,
      },

      yaxis: {
        min: 0,
        max: 100,

        labels: {
          formatter: (
            val: number
          ) =>
            `${val} %`,
        },
      },

tooltip: {
  shared: true,
  intersect: false,

  custom: function ({
    series,
    dataPointIndex,
    w,
  }) {
    if (
      !series ||
      !w.globals.seriesNames ||
      !w.globals.colors
    ) {
      return '';
    }

    const date =
      w.globals.labels[dataPointIndex] || '';

    const total =
      w.globals.stackedSeriesTotals[dataPointIndex];

    const collapsed = [
      ...(w.globals.collapsedSeriesIndices || []),
      ...(w.globals.ancillaryCollapsedSeriesIndices || []),
    ];

    const rows = series
      .map((s: any, i: number) => {
        if (collapsed.includes(i)) {
          return '';
        }

        const value = s[dataPointIndex];

        const percentage =
          total
            ? ((value / total) * 100).toFixed(1)
            : 0;

        const formattedValue =
          value != null && !isNaN(value)
            ? `${formatNumber(value)} (${fPercent(percentage)})`
            : '0';

        return `
          <div
            style="
              display:flex;
              align-items:center;
              width:100%;
              box-sizing:border-box;
              padding:4px;
              gap:8px;
            "
          >
            <span
              style="
                display:inline-block;
                width:4px;
                height:20px;
                border-radius:12px;
                background-color:${w.globals.colors[i]};
                flex-shrink:0;
              "
            ></span>

            <div
              style="
                flex:1;
                min-width:0;
                display:flex;
                justify-content:space-between;
                align-items:center;
                font-family:Public Sans,sans-serif;
                font-size:13px;
                font-weight:500;
                color:#333C47;
                white-space:nowrap;
                gap:16px;
              "
            >
              <span>
                ${w.globals.seriesNames[i]}
              </span>

              <span
                style="
                  flex-shrink:0;
                  text-align:right;
                "
              >
                ${formattedValue}
              </span>
            </div>
          </div>
        `;
      })
      .join('');

    const pointValues =
      series.map(
        (s: any) =>
          Number(s?.[dataPointIndex]) || 0
      );

    const pointNames =
      w.globals.seriesNames || [];

    const extraRows =
      extraTooltipRows &&
      extraTooltipRows.length
        ? extraTooltipRows
            .map((r) => {
              const value =
                r.compute(
                  pointValues,
                  pointNames
                );

              const formattedValue =
                value != null && !isNaN(value)
                  ? formatNumber(value)
                  : '0';

              return `
                <div
                  style="
                    display:flex;
                    align-items:center;
                    width:100%;
                    box-sizing:border-box;
                    padding:4px;
                    gap:8px;
                  "
                >
                  <span
                    style="
                      display:inline-block;
                      width:4px;
                      height:20px;
                      border-radius:12px;
                      background-color:${r.color};
                      flex-shrink:0;
                    "
                  ></span>

                  <div
                    style="
                      flex:1;
                      min-width:0;
                      display:flex;
                      justify-content:space-between;
                      align-items:center;
                      font-family:Public Sans,sans-serif;
                      font-size:13px;
                      font-weight:500;
                      color:#333C47;
                      white-space:nowrap;
                      gap:16px;
                    "
                  >
                    <span>
                      ${r.name}
                    </span>

                    <span
                      style="
                        flex-shrink:0;
                        text-align:right;
                      "
                    >
                      ${formattedValue}
                    </span>
                  </div>
                </div>
              `;
            })
            .join('')
        : '';

    return `
      <div
        style="
          width:320px;
          min-width:320px;
          max-width:320px;
          box-sizing:border-box;

          background:#FCFCFD;

          border:none;
          border-radius:12px;

          box-shadow:0px 4px 8px rgba(145,158,171,0.16);

          overflow:hidden;
        "
      >
        <div
          style="
            padding:12px 12px 4px 12px;
            box-sizing:border-box;
          "
        >
          <p
            style="
              font-family:Public Sans,sans-serif;
              color:#333C47;
              font-size:14px;
              font-weight:500;
              line-height:14px;
              white-space:nowrap;
              margin:0;
              margin-bottom:12px;
            "
          >
            ${date}
          </p>
        </div>

        <div
          style="
            padding:0 12px 12px 12px;
            display:flex;
            flex-direction:column;
            box-sizing:border-box;
          "
        >
          ${rows}
          ${extraRows}
        </div>
      </div>
    `;
  },

  y: {
    formatter: (
      val: number,
      {
        dataPointIndex,
        w,
      }
    ) => {
      const total =
        w.globals.stackedSeriesTotals[dataPointIndex];

      const percentage =
        total
          ? ((val / total) * 100).toFixed(2)
          : 0;

      return `${val.toFixed(0)} (${percentage}%)`;
    },
  },
},

      annotations: {
        xaxis:
          annotations,

        points:
          labelAnnotations,
      },
    });

  return (
    <div
      ref={
        chartContainerRef
      }
      style={{
        width: '100%',
      }}
    >
      <Chart
        ref={chartRef}
        dir="ltr"
        type="bar"
        series={series}
        options={
          chartOptions
        }
        height={height}
      />
    </div>
  );
}